import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLocation, Link } from "react-router-dom";
import { ArrowUpRight, X } from "lucide-react";

/*
 * ContinueWhisper
 * ---------------
 * A minimalist brass pill that fades in on the Home page after the
 * reader has scrolled far enough. Proposes the next natural read:
 * Field Notes or the Founder letter.
 *
 * Rhythm-personalised trigger
 * ---------------------------
 * We track the reader's dominant scroll velocity (pixels per second)
 * with an exponential moving average across the session. The whisper
 * then adapts:
 *   - Slow, deep readers  -> fire earlier (they have already absorbed
 *                            each scene deeply).
 *   - Fast skimmers       -> fire later, and only after a real dwell
 *                            (do not interrupt a scroll-past).
 * A short "reader stillness" check (no scroll for ~900ms) is required
 * across every rhythm bucket, so the pill never appears mid-scroll.
 *
 * Dismissable with a 7-day cool-down persisted in localStorage. Never
 * interrupts the story, only offers a soft next-step.
 */
const COOLDOWN_KEY = "gvg-continue-whisper";
const COOLDOWN_DAYS = 7;

// Rhythm-tuned scroll thresholds. Slow readers are rewarded sooner.
const TRIGGER_SLOW = 0.78;
const TRIGGER_AVG = 0.88;
const TRIGGER_FAST = 0.94;

// Velocity bucket bounds in pixels per second.
const VELOCITY_SLOW_MAX = 380;
const VELOCITY_FAST_MIN = 1400;

// The reader must sit still for this long after crossing the threshold
// before the whisper appears. Prevents interrupting a fast scroll-past.
const DWELL_MS = 900;

function isCoolingDown() {
  try {
    const raw = localStorage.getItem(COOLDOWN_KEY);
    if (!raw) return false;
    const until = parseInt(raw, 10);
    return Number.isFinite(until) && Date.now() < until;
  } catch (e) {
    return false;
  }
}

function setCooldown() {
  try {
    const until = Date.now() + COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(COOLDOWN_KEY, String(until));
  } catch (e) {
    /* noop */
  }
}

export default function ContinueWhisper() {
  const { pathname } = useLocation();
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (pathname !== "/") {
      setVisible(false);
      return undefined;
    }
    if (isCoolingDown()) return undefined;

    // Rolling exponential moving average of scroll velocity (px/sec).
    // Weighted toward the reader's *dominant* rhythm, not the last flick.
    let emaVelocity = 0;
    let lastY = window.scrollY;
    let lastT = performance.now();
    let dwellTimer = null;

    const clearDwell = () => {
      if (dwellTimer) {
        window.clearTimeout(dwellTimer);
        dwellTimer = null;
      }
    };

    const triggerFor = (v) => {
      if (v < VELOCITY_SLOW_MAX) return TRIGGER_SLOW;
      if (v > VELOCITY_FAST_MIN) return TRIGGER_FAST;
      return TRIGGER_AVG;
    };

    const check = () => {
      const now = performance.now();
      const y = window.scrollY;
      const dt = Math.max(16, now - lastT); // ms, floor at ~1 frame
      const dy = Math.abs(y - lastY);
      const instant = (dy / dt) * 1000; // px per second
      // Alpha 0.12 -> slow to react so brief bursts do not skew rhythm.
      emaVelocity = emaVelocity === 0 ? instant : emaVelocity * 0.88 + instant * 0.12;
      lastT = now;
      lastY = y;

      const doc = document.documentElement;
      const scrolled = y + window.innerHeight;
      const height = doc.scrollHeight;
      const ratio = height > 0 ? scrolled / height : 0;
      const trigger = triggerFor(emaVelocity);

      clearDwell();
      if (ratio >= trigger) {
        // Only reveal once the reader actually settles.
        dwellTimer = window.setTimeout(() => setVisible(true), DWELL_MS);
      }
    };

    window.addEventListener("scroll", check, { passive: true });
    check();
    return () => {
      window.removeEventListener("scroll", check);
      clearDwell();
    };
  }, [pathname]);

  if (pathname !== "/") return null;

  const onGo = () => setCooldown();
  const onDismiss = () => {
    setCooldown();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="continue-whisper"
          data-testid="home-continue-whisper"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="fixed left-1/2 -translate-x-1/2 bottom-6 md:bottom-10 z-40 w-[min(520px,calc(100vw-32px))]"
        >
          <div
            className="relative rounded-2xl border border-brass/40 bg-black/70 backdrop-blur-xl px-5 py-4 md:px-6 md:py-5 overflow-hidden"
            style={{
              boxShadow:
                "0 24px 60px -20px rgba(0,0,0,0.6), 0 0 40px rgba(201,169,97,0.14), inset 0 1px 0 rgba(245,241,232,0.05)",
            }}
          >
            <span
              aria-hidden
              className="absolute inset-x-6 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(201,169,97,0.65), transparent)",
              }}
            />
            <div className="relative flex items-center gap-4">
              <motion.span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full bg-brass shrink-0"
                animate={reduced ? {} : { opacity: [1, 0.35, 1] }}
                transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: [0.4, 0, 0.6, 1],
                }}
              />

              <div className="flex-1 min-w-0">
                <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
                  If any of this landed
                </p>
                <p className="mt-1.5 font-serif italic text-ink text-[15px] leading-snug">
                  Keep reading.
                </p>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  <Link
                    to="/field-notes"
                    onClick={onGo}
                    data-testid="continue-whisper-notes"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brass hover:bg-brass/85 px-3.5 py-1.5 font-sans text-[10px] uppercase tracking-mega font-semibold text-bg transition-colors duration-500"
                  >
                    Read Field Notes
                    <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>

              <button
                type="button"
                onClick={onDismiss}
                data-testid="continue-whisper-dismiss"
                aria-label="Dismiss"
                className="shrink-0 text-ink-muted hover:text-brass transition-colors duration-500 self-start mt-0.5"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
